import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function VariationClause() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link to="/" data-testid="home-link" className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      <div className="h-32 md:h-48" style={{ background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.background} 50%, ${colors.backgroundLight} 100%)` }} />

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 data-testid="page-title" className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8" style={{ color: colors.text }}>
            What Does a Variation Clause Mean?
          </h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                A variation clause sets out how the contract can be modified or amended after it's been signed.
              </p>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Example Clause</h2>
              <div className="p-6 rounded-xl text-base" style={{ backgroundColor: colors.backgroundDark, color: colors.text, fontFamily: 'monospace' }}>
                "This Agreement may not be varied, modified, or amended except by written instrument signed by authorized representatives of both parties. No oral modifications shall be valid. Any variation must specifically reference this clause and identify the provisions being modified. Electronic signatures are acceptable for variations."
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Plain-English Explanation</h2>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                This clause protects both parties by establishing clear rules for changes:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Changes must be documented in writing (emails may or may not count)
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Both parties must agree to modifications
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Verbal agreements or side deals typically don't count
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Prevents unauthorized people from making changes
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Notable Wording</h2>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>"May not be varied except"</strong> - creates a strict requirement
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>"Authorized representatives"</strong> - not just anyone can make changes
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>"No oral modifications"</strong> - verbal agreements don't count
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Common Purpose</h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Variation clauses prevent misunderstandings and disputes about whether the contract was changed. They ensure both parties consciously agree to modifications rather than relying on informal conversations or assumptions. This protects against unauthorized changes and creates a clear audit trail. It's particularly important in long-term contracts where circumstances may evolve.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Typical Effects</h2>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Casual agreements or email exchanges may not modify the contract
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Changes require formal process and documentation
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Protects against misunderstandings about what was agreed
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  May slow down needed changes if process is overly formal
                </li>
              </ul>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Courts may sometimes enforce oral modifications despite a written variation clause, particularly if both parties clearly acted on the verbal agreement. The definition of "written" may be debated—texts, emails, or other electronic communications might qualify. Some jurisdictions recognize implied variations through conduct. What counts as an "authorized representative" should be clearly defined. This explanation covers typical contract language but doesn't constitute legal advice about your specific situation.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>Related Clauses</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link to="/explain-governing-law-clause" data-testid="related-link-governing-law" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Governing Law</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Learn about jurisdiction choice</p>
                </Link>
                <Link to="/explain-cancellation-clause" data-testid="related-link-cancellation" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Cancellation Clause</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Understand ending agreements</p>
                </Link>
              </div>
            </section>

            <div data-testid="cta-section" className="text-center p-8 rounded-2xl" style={{ borderColor: colors.border }}>
              <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
              <Link to="/" data-testid="cta-button" className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all" style={{ backgroundColor: '#2F3E30' }}>
                Try ClauseClear
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Sponsorship Placeholder */}
            <SponsorPlaceholder variant="full" />
          </div>
        </div>
      </article>

      <footer className="px-6 md:px-12 lg:px-24 py-12 text-center " style={{ borderColor: colors.border }}>
        <p className="text-sm mb-2" style={{ color: colors.textMuted }}>ClauseClear is an educational tool. It does not provide legal advice.</p>
        <a href="https://buymeacoffee.com/jamesmoneyz" target="_blank" rel="noopener noreferrer" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          Support the project
        </a>
      </footer>
    </div>
  );
}
